import { IconButton, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import type React from 'react';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { addFile as apiAddFile, deleteFile as apiDeleteFile } from '~/api/projects2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project as BaseProject } from '~/common/types';
import Icon from '~/components2/Icon';
import Popover from '~/components2/Popover/Popover';
import { useGetProject } from '~/hooks/useGetProject';
import CustomDataSection from './CustomDataSection';
import { fileTypes, mediaTypes, type ProjectFile } from './common';
import FileItem from './FileItem';

interface ProjectWithFiles extends BaseProject {
  files: ProjectFile[];
}

interface UploadButtonProps {
  accept: string[];
  onUpload: (accepted: any, rejected: any) => void;
  processing: boolean;
  children: React.ReactNode;
  'data-testid'?: string;
}

const UploadButton = ({ accept, onUpload, children, processing, 'data-testid': testId }: UploadButtonProps) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onUpload,
    maxSize: 1073741824, // max file size 1 GB;
  });

  return (
    <Can action={Action.AddProjectFile}>
      <IconButton
        {...getRootProps()}
        icon="upload"
        variant="primary"
        loading={processing}
        disabled={processing}
        data-testid={testId}
      >
        {children}
        <input {...getInputProps({ accept: accept.join() })} />
      </IconButton>
    </Can>
  );
};

interface TitleSectionProps {
  title: string;
  accept: string[];
  onUpload: (accepted: File[], rejected: File[]) => Promise<void>;
  processing: boolean;
  uploadTestId?: string;
  acceptedTypesTestId?: string;
}

const TitleSection = ({
  title,
  accept,
  onUpload,
  processing,
  uploadTestId,
  acceptedTypesTestId,
}: TitleSectionProps) => {
  return (
    <section className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="font-semibold">{title}</div>
        <Popover
          trigger={
            <button
              type="button"
              aria-label={`${title} accepted file types`}
              data-testid={acceptedTypesTestId}
              className="cursor-pointer appearance-none border-0 bg-transparent p-0 rounded focus-ring"
            >
              <Icon icon="help" />
            </button>
          }
        >
          {(_close) => (
            <div className="shadow-bg-grey-70 flex max-w-[17rem] flex-col gap-4 rounded bg-white p-4 text-xs shadow-lg">
              <div>
                <span className="font-bold">Accepted File Types: </span>
                {accept.map((type) => `${type}, `)}
              </div>
            </div>
          )}
        </Popover>
      </div>
      <UploadButton accept={accept} onUpload={onUpload} processing={processing} data-testid={uploadTestId}>
        Upload Files
      </UploadButton>
    </section>
  );
};

export function MapChildren() {
  return null;
}

export function TabDetails() {
  const [processing, setProcessing] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const projectRequest = useGetProject<ProjectWithFiles>({ projectId }, { refetchOnWindowFocus: false });
  const project = projectRequest.data;
  const queryClient = useQueryClient();

  const onUpload = async (accepted: File[], _rejected: File[]) => {
    setProcessing(true);
    const uploadRequests = accepted.map(async (file: File) => {
      try {
        const data = new FormData();
        data.append('file', file);
        data.append('name', file.name);
        data.append('type', file.type);
        data.append('extension', file.name.split('.').pop()!);

        const uploadedFile = await apiAddFile(projectId, data);

        const type = file.type.startsWith('image') || file.type.startsWith('video') ? 'media' : 'files';

        capturePosthogEvent(POSTHOG_EVENTS.projectFileUploaded, {
          project_id: projectId,
          organization_id: project?.organization || undefined,
          filetype: type,
        });

        return uploadedFile;
      } catch {
        console.error('Error during file upload for file: ', file.name);
      }
    });

    await Promise.all(uploadRequests);
    queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });
    setProcessing(false);
    return;
  };

  const isMedia = (file: ProjectFile) => {
    return file.type.startsWith('image') || file.type.startsWith('video') || file.type === 'media';
  };

  const deleteFile = async (file: ProjectFile, type: 'files' | 'media') => {
    try {
      await apiDeleteFile(projectId, file.id);
      queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });

      capturePosthogEvent(POSTHOG_EVENTS.projectFileDeleted, {
        project_id: projectId,
        organization_id: project?.organization || undefined,
        filetype: type,
      });
    } catch (err: any) {
      console.error('Error while deleting file: ', err);
    }
  };

  if (projectRequest.isLoading || !project) {
    return <SkeletonSpinnerCentered />;
  }

  return (
    <div className="flex w-full min-w-0 flex-col">
      <div className="relative min-h-[40vh]">
        <TitleSection
          title="Project Files"
          accept={fileTypes}
          onUpload={onUpload}
          processing={processing}
          uploadTestId="project-files-upload-button"
          acceptedTypesTestId="project-files-accepted-types-trigger"
        />
        <div className="p-4 flex flex-col gap-2">
          {project.files.map((file) => {
            if (!isMedia(file)) {
              return (
                <FileItem
                  key={`${file.name || ''}-${file.uploadedAt}`}
                  file={file}
                  deleteFile={deleteFile}
                  fileType="files"
                  loadProject={() => queryClient.invalidateQueries({ queryKey: ['project', { projectId }] })}
                />
              );
            }
            return null;
          })}
        </div>
      </div>

      <div className="relative min-h-[10rem]">
        <TitleSection
          title="Site Photos & Videos"
          accept={mediaTypes}
          onUpload={onUpload}
          processing={processing}
          uploadTestId="project-media-upload-button"
          acceptedTypesTestId="project-media-accepted-types-trigger"
        />

        <div className="p-4 flex flex-col gap-2">
          {project.files.map((file) => {
            if (isMedia(file)) {
              return (
                <FileItem
                  key={`${file.name || ''}-${file.uploadedAt}`}
                  file={file}
                  deleteFile={deleteFile}
                  fileType="media"
                  loadProject={() => queryClient.invalidateQueries({ queryKey: ['project', { projectId }] })}
                />
              );
            }
            return null;
          })}
        </div>
      </div>

      {/* Admin-only, flag-gated; renders null otherwise. */}
      <CustomDataSection />
    </div>
  );
}
